import { Patient, Pathway, AdminTypeV2, DispositionType } from '@doc-abode/data-models';
import { findPathway, findService } from '../config';
import { findAdminType } from '../config/adminTypeHelpers';

/**
 * Utility to allow checking whether a visit should be synced to S1
 * @param pathwayToCheck string value of the pathway of a job
 * @param serviceToCheck string value of the service (aka disposition) of a job
 * @param pathways array of available pathways
 * @returns boolean - true if the pathway/service explicitly indicate a sync to S1; false otherwise
 */
function shouldVisitBeSyncedToS1(
    pathwayToCheck: string,
    serviceToCheck: string,
    pathways: Pathway[],
): boolean {
    const pathwayConfig = findPathway(pathwayToCheck, pathways);
    let service;

    if (pathwayConfig) {
        service = findService(serviceToCheck, pathwayConfig.services);
    }

    if (service) {
        return service.syncToS1 || false;
    }
    return false;
}

/**
 * Utility to allow checking whether a visit should be synced to S1
 * @param adminTypeToCheck string value of the admin tyoe of a job
 * @param adminTypes array of available adminTypes
 * @returns boolean - true if the admin type explicitly indicate a sync to S1; false otherwise
 */
function shouldAdminBeSyncedToS1(adminTypeToCheck: string, adminTypes: AdminTypeV2[]): boolean {
    const adminConfig = findAdminType(adminTypeToCheck, adminTypes);

    if (adminConfig) {
        return adminConfig.syncToS1 || false;
    }

    return false;
}

/**
 * Utility to evaluate whether a job should be synced to S1
 * @param job the job for which to check
 * @param pathways array of pathways
 * @param adminTypes array of admin types
 * @returns true if the job is supposed to be synced to S1, false if the job is not synced to S1
 */
export function shouldBeSyncedToS1(
    job: Patient,
    pathways: Pathway[],
    adminTypes: AdminTypeV2[],
): boolean {
    // Check the configuration for visits
    if (job.referralPathway && job.disposition) {
        return shouldVisitBeSyncedToS1(job.referralPathway, job.disposition, pathways);
    }

    // Check the configuration for admin jobs
    if (job.disposition === DispositionType.ADMIN && job.activityType) {
        return shouldAdminBeSyncedToS1(job.activityType, adminTypes);
    }

    // Any invalid combination
    return false;
}
